export type ValidationSeverity = 'error' | 'warning' | 'information';

export type ValidationIssue = {
  message: string;
  severity?: ValidationSeverity;
};

export const isValidationIssue = (value: unknown): value is ValidationIssue => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.message === 'string';
};

export const getValidationIssueMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (isValidationIssue(value)) {
    return value.message;
  }

  return undefined;
};

export const getValidationIssueSeverity = (value: unknown): ValidationSeverity => {
  if (isValidationIssue(value) && value.severity) {
    return value.severity;
  }

  return 'error';
};

export const getValidationIssuesCount = (value: unknown): number => {
  if (value == null) {
    return 0;
  }

  if (typeof value === 'string' || isValidationIssue(value)) {
    return 1;
  }

  if (typeof value !== 'object') {
    return 0;
  }

  const values = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);
  return values.reduce((accumulator: number, item) => accumulator + getValidationIssuesCount(item), 0);
};

export const getHighestValidationSeverity = (value: unknown): ValidationSeverity => {
  const severities = collectSeverities(value);
  if (severities.includes('error')) {
    return 'error';
  }

  if (severities.includes('warning')) {
    return 'warning';
  }

  return 'information';
};

const collectSeverities = (value: unknown): ValidationSeverity[] => {
  if (value == null) {
    return [];
  }

  if (typeof value === 'string') {
    return ['error'];
  }

  if (isValidationIssue(value)) {
    return [getValidationIssueSeverity(value)];
  }

  if (typeof value !== 'object') {
    return [];
  }

  const values = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);
  return values.flatMap((item) => collectSeverities(item));
};
